/*
  Overflow marquee

  <overflow-marquee> shows its text as is while it fits. When the text is wider than the element (a long gift name on
  the free gift card, a translated label), it scrolls as a seamless marquee: a copy follows the text and the pair moves
  left in a loop at a constant speed. It checks again whenever its size changes, so it also works when a card resizes or
  the element is rendered later (product cards, quick buy, cart drawer).

  Markup: <overflow-marquee class="..."><span class="overflow-marquee__track"><span class="overflow-marquee__text">
  Text</span></span></overflow-marquee>

  With reduced motion the text does not move; it is cut off with an ellipsis instead (custom.css).
*/

const PIXELS_PER_SECOND = 30;

class OverflowMarquee extends HTMLElement {
  #observer = null;
  #frame = 0;

  connectedCallback() {
    this.#observer = new ResizeObserver(() => this.#schedule());
    this.#observer.observe(this);
    document.fonts?.ready.then(() => this.#schedule());
    this.#schedule();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const track = this.querySelector(':scope > .overflow-marquee__track');
    const text = track?.querySelector(':scope > .overflow-marquee__text');

    if (!text) {
      return;
    }

    const style = getComputedStyle(this);
    const available = this.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const overflowing = text.scrollWidth > Math.ceil(available) + 1;

    track.querySelector(':scope > .overflow-marquee__copy')?.remove();
    this.classList.toggle('is-overflowing', overflowing);

    if (!overflowing) {
      return;
    }

    const copy = text.cloneNode(true);
    copy.classList.add('overflow-marquee__copy');
    copy.setAttribute('aria-hidden', 'true');
    track.appendChild(copy);

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    this.style.setProperty('--overflow-marquee-duration', `${(text.scrollWidth + gap) / PIXELS_PER_SECOND}s`);
  }
}

if (!customElements.get('overflow-marquee')) {
  customElements.define('overflow-marquee', OverflowMarquee);
}
